(ns web3.errors
  (:require [web3.utils :as utils :include-macros true]
            [web3.log :as log]
            [web3.i18n :as i]
            [re-frame.core :as rf]
            [web3.tracking.sentry :as sentry]))

;; Error handling
;;
;; Overall goals are:
;;
;; - Display friendly messages to users
;; - Display an error's message close to its source
;; - Escalate serious or unanticipated errors
;;   - Report (with emphasis) via HoneyComb
;;
;; Data format
;;
;; - When errors are caught, they should always be passed/reported as
;;   a map of the form:
;;
;;   {:source  <keyword>  eg. :error/graphql, :error/okta
;;    :error   <error>
;;    …other data…}
;;

(defmulti parse-error
  "Given an error map, adds :message and :severity.

   A multimethod, for domain-specific implementations dispatched on :source.

   :severity should be one of: (TBD)
     :critical - unexpected during ordinary operations, should be escalated to attention of development
     :high
     :warning
     :debug
     :info"
  :source)

(defn error-metadata
  "Add default :message and :severity to error, if not provided."
  [{:keys [source message error severity] :as data}]
  (merge data
         {:message (or (utils/some-str message)
                       (str (i/get :errors/unknown)
                            (when-let [error-message (some-> error (.-message) (utils/some-str))]
                              (str "\n Info: "
                                   error-message))))
          :severity (or severity :high)}))

(defn report-error!
  "Given a parsed error map containing `:message` and `:severity`,
   report infraction to relevant authorities."
  [{:keys [source error message severity debug-log] :as data}]
  (assert (and error severity))

  (let [level (case severity
                (:critical :high) :error
                (:medium :moderate) :warn
                :low :debug)]
    (log/group-start-collapsed :warn (str "Error(" (name severity) "): " source))
    (some->> message
             (log/println level))
    (some->> debug-log
             (log/prn level))
    (some->> (.-stack error)
             (log/log :error))
    (log/group-end :warn)
    (sentry/capture-exception error)))

(rf/reg-event-fx :events/handle-error

  ;; Errors flow through this central point, so that we can
  ;; escalate serious/unknown errors and ensure consistent formatting
  ;; (ie. friendly messages).

  ;; :severity should be one of:
  ;;   :critical
  ;;   :error
  ;;   :warning
  ;;   :debug
  ;;   :info

  (fn [{:keys [db]} [_ {:keys [source
                               error
                               debug-log

                               ;; pass db-path or dispatch for further side effects.
                               db-path
                               dispatch] :as error-data}]]

    ;; error data _must_ include `:source` and `:error` keys.
    (assert (and source error))

    (let [error-data
          (-> error-data

              ;; augment with default :message and :severity
              (merge (error-metadata error-data))

              ;; Remove re-frame instructions.
              (dissoc :db-path :dispatch))]

      ;; Logging, with escalation based on :severity
      (report-error! error-data)

      ;; Error data may include `:db-path` and `:dispatch` keys for further side-effects.
      ;; `:db-path` is used to write the _augmented_ error data into the db.
      (cond-> {}
              db-path (assoc :db (assoc-in db db-path error-data))
              dispatch (assoc :dispatch dispatch)))))
